import { HTMLAttributes, forwardRef } from 'react';
import { colors, spacing } from '@ardoq/design-tokens';
import { text1, typographyMixins } from '@ardoq/typography';
import styled, { css } from 'styled-components';
import { markdownToSanitizedHTML } from '../markdown-to-html/markdownToSanitizedHTML';
import { SanitationLevel } from '@ardoq/html';
import type { MarkdownFlavor } from '../markdownFlavor';
import { IMAGE_SIZE_PRESET_WIDTHS } from '../imageSizes';

const StyledHTML = styled.div`
  line-height: 1.4;
  white-space: normal;

  h1 {
    ${typographyMixins.header1}
    font-size: 24px;
    line-height: 28px;
    margin: 20px 0 20px;
    padding-bottom: 7px;
  }

  h2 {
    ${typographyMixins.header2}
    font-size: 22px;
    line-height: 23px;
    margin: 27px 0 19px;
    padding-bottom: 7px;
  }

  h3 {
    ${typographyMixins.header3}
    font-size: 20px;
    margin: 26px 0 9px;
    line-height: 18px;
  }

  h4 {
    ${typographyMixins.header4}
    font-size: 18px;
    margin: 16px 0 7px;
    line-height: 18px;
  }

  h5 {
    ${typographyMixins.header5}
    text-transform: none;
    font-size: 16px;
    line-height: 17px;
    margin: 14px 0 0;
  }

  h6 {
    font-size: 14px;
    line-height: 17px;
    margin: 9px 0 -2px;
  }

  hr {
    overflow: hidden;
  }
  blockquote {
    margin: 0;
    border-left: 4px solid ${colors.borderSubtle};
    padding: 0 ${spacing.s16};
    color: ${colors.textSubtle};
  }

  blockquote > :first-child {
    margin-top: 0;
  }

  blockquote > :last-child {
    margin-bottom: 14px;
  }

  pre {
    font-weight: 400;
    font-size: 13px;
    font-family: Courier, monospace;
    margin: 2px 0 8px;
    padding: 18px;
    background-color: ${colors.bgSubtle};
  }

  code {
    font-family: Courier, monospace;
    font-weight: 400;
    font-size: 14px;
    color: ${colors.textDefault};
    background-color: ${colors.bgSubtle};
    padding: 2px 3px;
  }

  pre code {
    padding: 0;
    color: inherit;
    white-space: pre-wrap;
    background-color: transparent;
  }

  img {
    vertical-align: top;
    max-width: 100%;
    height: auto;
  }

  ${Object.entries(IMAGE_SIZE_PRESET_WIDTHS).map(
    ([size, width]) => css`
      img[data-size='${size}'] {
        max-width: ${width};
      }
    `
  )}

  ul,
  ol {
    display: block;
    padding-left: ${spacing.s24};
    margin: 6px 0 10px;
  }

  ul > li {
    list-style: disc;
  }

  ol > li {
    list-style: decimal;
  }

  ul ul,
  ul ol,
  ol ol,
  ol ul {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  ul p,
  ol p {
    margin: 0;
  }

  ol li::marker {
    color: ${colors.textSubtle};
  }

  a {
    color: ${colors.textAction};
    text-decoration: underline;
  }

  table {
    width: 100%;
    color: ${colors.textDefault};
    border-collapse: collapse;
    th,
    td {
      padding: 5px 14px 5px 12px;
      height: 32px;
    }

    th {
      ${text1};
      background-color: ${colors.bgInverted};
      border: 1px solid ${colors.borderSubtle};
      color: ${colors.textOnInvertedStrong};

      a {
        color: ${colors.textOnInvertedStrong};
        text-decoration: underline;
      }
    }

    td {
      border: 1px solid ${colors.borderSubtle};
    }
  }
`;

type MarkdownViewerProps = {
  content: string;
  sanitationLevel: SanitationLevel;
  transformHTML?: (sanitizedHTML: string) => string;
  markdownFlavor?: MarkdownFlavor;
} & HTMLAttributes<HTMLDivElement>;

export const MarkdownViewer = forwardRef<HTMLDivElement, MarkdownViewerProps>(
  (props, ref) => {
    const { content, transformHTML, sanitationLevel, markdownFlavor, ...rest } =
      props;
    const sanitizedHTML = markdownToSanitizedHTML(
      content,
      sanitationLevel,
      markdownFlavor
    );
    const html = transformHTML ? transformHTML(sanitizedHTML) : sanitizedHTML;

    return (
      <StyledHTML
        ref={ref}
        // `sanitizedHTML` is DOMPurify-sanitised; callers passing `transformHTML`
        // are responsible for keeping its output safe.
        // oxlint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
        {...rest}
      />
    );
  }
);
